import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n";
import { useAuth } from "@/auth";
import { api } from "@/lib/api";
import Logo from "@/components/Logo";
import BrandToolbar from "@/components/BrandToolbar";
import { useCurrency } from "@/components/Price";
import { SHOW_FOUNDERS_PROGRAM } from "@/config/features";
import { getPlanFeatureKeys } from "@/config/planFeatures";
import {
  Lock,
  MessageSquareHeart,
  HeartPulse,
  Users,
  Check,
  X,
  ArrowRight,
  Sparkles,
  Shield,
  Globe,
  KeyRound,
  Archive,
  UserCheck,
  HeartHandshake,
  ScrollText,
  Siren,
} from "lucide-react";

const HERO_BG =
  "https://images.pexels.com/photos/21031387/pexels-photo-21031387.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const MEM_1 =
  "https://images.unsplash.com/photo-1761030293423-130d4c937ead?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHw0fHxmYW1pbHklMjBtZW1vcnklMjBzaWxob3VldHRlJTIwc3Vuc2V0fGVufDB8fHx8MTc3ODUxNjkzMXww&ixlib=rb-4.1.0&q=85";
const VAULT_TEX =
  "https://images.unsplash.com/photo-1647101734210-ff5d79813069?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxkYXJrJTIwZ29sZCUyMGFic3RyYWN0JTIwdGV4dHVyZXxlbnwwfHx8fDE3Nzg1MTY5MzF8MA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const { t } = useI18n();
  const { symbol } = useCurrency();
  const { user } = useAuth();

  // Live Lifetime slots — when sold out we hide the card entirely
  // so the public landing reflects the same source of truth as /plans.
  const [slots, setSlots] = useState({ used: 0, total: 100, available: 100, is_available: true });
  useEffect(() => {
    api.get("/lifetime-slots").then((r) => setSlots(r.data)).catch(() => {});
  }, []);

  return (
    <div className="bg-obsidian text-white min-h-screen">
      {/* Navbar */}
      <nav className="glass-header fixed top-0 left-0 right-0 z-50 pt-safe">
        <div className="max-w-7xl mx-auto px-3 sm:px-8 py-3 sm:py-5 flex items-center justify-between gap-2">
          <Link to="/" data-testid="landing-home-logo"><Logo size={24} className="sm:!gap-3 shrink-0" /></Link>
          <div className="hidden md:flex items-center gap-8 lg:gap-10 text-sm text-muted">
            <a href="#features" className="hover:text-gold-light transition-colors" data-testid="nav-features">
              {t("nav_features")}
            </a>
            <Link to="/plans" className="hover:text-gold-light transition-colors" data-testid="nav-pricing">
              {t("nav_pricing")}
            </Link>
            {SHOW_FOUNDERS_PROGRAM && (
              <Link to="/founders-program" className="hover:text-gold-light transition-colors" data-testid="nav-founders">
                {t("nav_founders")}
              </Link>
            )}
            <a href="#security" className="hover:text-gold-light transition-colors" data-testid="nav-security">
              {t("nav_security")}
            </a>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
            <BrandToolbar />
            {user ? (
              <Link
                to="/dashboard"
                className="btn-gold px-2.5 sm:px-5 py-1.5 sm:py-2.5 text-xs sm:text-sm whitespace-nowrap"
                data-testid="nav-dashboard-btn"
              >
                {t("nav_dashboard_short")} →
              </Link>
            ) : (
              <>
                <Link
                  to="/login"
                  className="btn-ghost px-2.5 sm:px-5 py-1.5 sm:py-2.5 text-xs sm:text-sm whitespace-nowrap"
                  data-testid="nav-login-btn"
                >
                  {t("nav_login")}
                </Link>
                <Link
                  to="/login?signup=1"
                  className="btn-gold px-2.5 sm:px-5 py-1.5 sm:py-2.5 text-xs sm:text-sm whitespace-nowrap hidden lg:inline-flex"
                  data-testid="nav-signup-btn"
                >
                  {t("nav_signup_cta")}
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-32 sm:pt-40 pb-32 overflow-hidden hero-bg">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage: `url(${HERO_BG})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            maskImage:
              "radial-gradient(ellipse at center, rgba(0,0,0,0.9) 0%, transparent 70%)",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-obsidian/40 via-obsidian/70 to-obsidian" />

        <div className="relative max-w-6xl mx-auto px-6 sm:px-8 fade-in">
          <div className="font-label text-gold mb-6 sm:mb-8" data-testid="hero-label">
            <Sparkles className="w-3 h-3 inline mr-2" />
            {t("brand_tagline")}
          </div>
          <h1 className="font-display text-5xl sm:text-6xl md:text-8xl lg:text-9xl leading-[0.95] mb-8 sm:mb-10 max-w-5xl">
            {t("hero_title_1")}
            <br />
            <span className="shimmer-text italic">{t("hero_title_2")}</span>
          </h1>
          <p className="text-lg sm:text-xl text-muted max-w-2xl leading-relaxed mb-10 sm:mb-14 font-body">
            {t("hero_sub")}
          </p>
          <div className="flex flex-wrap gap-3 sm:gap-4 items-center">
            <Link to="/login?signup=1" data-testid="hero-cta-btn">
              <button className="btn-gold">
                {t("cta_secure_legacy")} <ArrowRight className="w-4 h-4" />
              </button>
            </Link>
            <a href="#how-it-works" className="btn-ghost">
              {t("cta_learn")}
            </a>
          </div>
          {/* Trust badges */}
          <div className="mt-10 flex flex-wrap gap-x-6 gap-y-2 text-xs text-faded" data-testid="hero-trust-badges">
            <span>{t("landing_trust_aes")}</span>
            <span>{t("landing_trust_gdpr")}</span>
            <span>{t("landing_trust_encrypted")}</span>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="py-24 sm:py-32 px-6 sm:px-8 max-w-7xl mx-auto" data-testid="landing-how-section">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <div className="font-label text-gold mb-6">{t("landing_how_label")}</div>
          <h2 className="font-display text-4xl sm:text-5xl md:text-6xl leading-tight">{t("landing_how_title")}</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: Archive, key: "1" },
            { icon: UserCheck, key: "2" },
            { icon: HeartHandshake, key: "3" },
          ].map(({ icon: Icon, key }) => (
            <div
              key={key}
              className="glass-card rounded-md p-8 relative overflow-hidden"
              data-testid={`landing-how-step-${key}`}
            >
              <div className="font-display text-7xl text-gold/15 absolute top-2 right-4 leading-none select-none">
                {key}
              </div>
              <Icon className="w-9 h-9 text-gold mb-6 relative" strokeWidth={1.2} />
              <h3 className="font-display text-2xl mb-3 text-gold-light relative">{t(`landing_how_${key}_title`)}</h3>
              <p className="text-muted leading-relaxed font-body text-sm relative">{t(`landing_how_${key}_body`)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Problem */}
      <section className="py-32 px-8 max-w-7xl mx-auto">
        <div className="grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-4">
            <div className="font-label text-gold mb-6">
              {t("section_problem_label")}
            </div>
            <h2 className="font-display text-5xl md:text-6xl leading-tight">
              {t("section_problem_title")}
            </h2>
          </div>
          <div className="md:col-span-7 md:col-start-6">
            <p className="text-xl text-muted leading-relaxed mb-12 font-body">
              {t("section_problem_body")}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
              <div className="border-t border-gold-subtle pt-6">
                <div className="font-display text-5xl text-gold mb-2">$86B</div>
                <div className="text-sm text-muted">{t("stat_crypto")}</div>
              </div>
              <div className="border-t border-gold-subtle pt-6">
                <div className="font-display text-5xl text-gold mb-2">1/3</div>
                <div className="text-sm text-muted">{t("stat_families")}</div>
              </div>
              <div className="border-t border-gold-subtle pt-6">
                <div className="font-display text-5xl text-gold mb-2">68%</div>
                <div className="text-sm text-muted">{t("stat_planning")}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="divider-gold max-w-7xl mx-auto" />

      {/* Features */}
      <section id="features" className="py-32 px-8 max-w-7xl mx-auto">
        <div className="mb-20 max-w-3xl">
          <div className="font-label text-gold mb-6">
            {t("section_solution_label")}
          </div>
          <h2 className="font-display text-5xl md:text-7xl leading-[1.05]">
            {t("section_solution_title")}
          </h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { icon: Lock, k: "vault", img: VAULT_TEX },
            { icon: MessageSquareHeart, k: "messages" },
            { icon: Users, k: "heirs", img: MEM_1 },
            { icon: HeartPulse, k: "pol" },
            { icon: ScrollText, k: "will" },
            { icon: Siren, k: "ice" },
          ].map((f) => {
            const Icon = f.icon;
            return (
              <div
                key={f.k}
                className="glass-card rounded-md p-8 relative overflow-hidden group hover:border-gold-subtle transition-all"
                data-testid={`feature-card-${f.k}`}
                style={{ minHeight: 320 }}
              >
                {f.img && (
                  <div
                    className="absolute inset-0 opacity-10 group-hover:opacity-20 transition-opacity"
                    style={{
                      backgroundImage: `url(${f.img})`,
                      backgroundSize: "cover",
                    }}
                  />
                )}
                <div className="relative">
                  <Icon className="w-8 h-8 text-gold mb-8" strokeWidth={1.2} />
                  <h3 className="font-display text-3xl mb-4 text-gold-light">
                    {t(`feature_${f.k}`)}
                  </h3>
                  <p className="text-muted leading-relaxed font-body">
                    {t(`feature_${f.k}_d`)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <div className="divider-gold max-w-7xl mx-auto" />

      {/* Security */}
      <section id="security" className="py-24 sm:py-28 px-6 sm:px-8 max-w-6xl mx-auto" data-testid="landing-security-section">
        <div className="text-center mb-14 max-w-3xl mx-auto">
          <div className="font-label text-gold mb-6">{t("landing_security_label")}</div>
          <h2 className="font-display text-4xl sm:text-5xl md:text-6xl leading-tight">{t("landing_security_title")}</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: KeyRound, k: "aes" },
            { icon: Globe, k: "gdpr" },
            { icon: Shield, k: "atrest" },
          ].map(({ icon: Icon, k }) => (
            <div key={k} className="glass-card rounded-md p-7" data-testid={`landing-security-${k}`}>
              <Icon className="w-9 h-9 text-gold mb-5" strokeWidth={1.2} />
              <h3 className="font-display text-xl mb-3 text-gold-light">{t(`landing_security_${k}_title`)}</h3>
              <p className="text-muted leading-relaxed font-body text-sm">{t(`landing_security_${k}_body`)}</p>
            </div>
          ))}
        </div>
      </section>

      <div className="divider-gold max-w-7xl mx-auto" />

      {/* Pricing */}
      <section id="pricing" className="pt-20 pb-12 sm:pt-24 sm:pb-16 px-6 sm:px-8 max-w-7xl mx-auto">
        <h2 className="font-display text-4xl sm:text-5xl md:text-6xl text-center mb-4">
          {t("pricing_title")}
        </h2>
        <p className="text-center text-muted text-sm mb-12 sm:mb-16">
          {t("pricing_no_card")}
        </p>
        <div className={`grid grid-cols-1 ${slots.is_available ? "md:grid-cols-3" : "md:grid-cols-2"} gap-5 max-w-5xl mx-auto items-stretch`}>
          {/* FREE */}
          <div className="glass-card rounded-md p-6 sm:p-7 flex flex-col relative border border-gold-subtle" data-testid="pricing-free">
            <h3 className="font-display text-2xl text-gold-light mb-1">{t("plan_free")}</h3>
            <p className="text-faded text-xs mb-4">{t("plan_free_sub")}</p>
            <div className="mb-1">
              <span className="font-display text-4xl">{symbol}0</span>
              <span className="text-faded text-sm"> {t("pricing_per_month_short")}</span>
            </div>
            <p className="text-[11px] text-faded/70 mb-5">{t("plan_free_billed_never")}</p>
            <ul className="space-y-2 mb-6 text-sm text-muted flex-1">
              {[
                { ok: true,  k: "plan_free_feat_vault" },
                { ok: true,  k: "plan_free_feat_pol" },
                { ok: true,  k: "plan_free_feat_heir" },
                { ok: true,  k: "plan_free_feat_contact" },
                { ok: true,  k: "plan_free_feat_assets" },
                { ok: true,  k: "plan_free_feat_storage" },
                { ok: false, k: "plan_free_feat_messages_blocked" },
                { ok: false, k: "plan_free_feat_ice_blocked" },
                { ok: false, k: "plan_free_feat_will_blocked" },
              ].map((row) => (
                <li key={row.k} className="flex items-start gap-2">
                  {row.ok
                    ? <Check className="w-4 h-4 text-gold mt-0.5 shrink-0" />
                    : <Lock className="w-4 h-4 text-faded/60 mt-0.5 shrink-0" />}
                  <span className={row.ok ? "" : "text-faded/70 line-through decoration-faded/40"}>
                    {t(row.k)}
                  </span>
                </li>
              ))}
            </ul>
            <Link to="/login?signup=1">
              <button className="btn-ghost w-full justify-center" data-testid="pricing-free-cta">
                {t("plan_free_cta_signup")}
              </button>
            </Link>
          </div>

          {/* PREMIUM — más popular */}
          <div className="glass-card rounded-md p-6 sm:p-7 flex flex-col relative ring-1 ring-gold border-2 border-gold gold-glow" data-testid="pricing-personal">
            <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-widest bg-gold text-obsidian px-3 py-1 rounded-full whitespace-nowrap">
              {t("plans_popular")}
            </span>
            <h3 className="font-display text-2xl text-gold-light mb-1">{t("plan_personal")}</h3>
            <p className="text-faded text-xs mb-4">{t("pricing_personal_sub")}</p>
            <div className="mb-1">
              <span className="font-display text-4xl">{symbol}39</span>
              <span className="text-faded text-sm">{t("pricing_per_year")}</span>
            </div>
            <p className="text-[11px] text-faded/70 mb-1">{t("pricing_billed_annually")}</p>
            <p className="text-xs text-gold-light/90 mb-5">≈ {symbol}3,25 {t("pricing_per_month_short")} · {t("plans_cancel_anytime")}</p>
            <ul className="space-y-2 mb-6 text-sm text-muted flex-1">
              {getPlanFeatureKeys("premium").map((k) => (
                <li key={k} className="flex items-start gap-2"><Check className="w-4 h-4 text-gold mt-0.5 shrink-0" /> {t(k)}</li>
              ))}
            </ul>
            <Link to="/login?signup=1&plan=personal_annual">
              <button className="btn-gold w-full justify-center">{t("pricing_cta_start_personal")}</button>
            </Link>
          </div>

          {/* LIFETIME FOUNDERS — hide completely when sold out */}
          {slots.is_available && (
            <div
              className="glass-card rounded-md p-6 sm:p-7 flex flex-col relative border-2 border-gold"
              style={{ background: "linear-gradient(135deg, rgba(201,163,106,0.06), rgba(201,163,106,0.01))" }}
              data-testid="pricing-lifetime"
            >
              <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-widest bg-red-500 text-white px-3 py-1 rounded-full whitespace-nowrap">
                {t("pricing_lifetime_founders_badge")}
              </span>
              <h3 className="font-display text-2xl text-gold-light mb-1">{t("pricing_lifetime_label")}</h3>
              <p className="text-faded text-xs mb-4">{t("pricing_lifetime_sub")}</p>
              <div className="mb-1">
                <span className="font-display text-4xl">{symbol}149</span>
                <span className="text-faded text-sm">{t("pricing_lifetime_once")}</span>
              </div>
              <p className="text-xs text-gold mb-5" data-testid="landing-lifetime-slots">
                {t("plans_lifetime_slots_avail").replace("{N}", slots.available).replace("{TOTAL}", slots.total)}
              </p>
              <ul className="space-y-2 mb-6 text-sm text-muted flex-1">
                {getPlanFeatureKeys("lifetime").map((k) => (
                  <li key={k} className="flex items-start gap-2"><Check className="w-4 h-4 text-gold mt-0.5 shrink-0" /> {t(k)}</li>
                ))}
              </ul>
              <Link to="/login?signup=1&plan=lifetime">
                <button className="btn-gold w-full justify-center">{t("pricing_cta_lifetime_short")}</button>
              </Link>
            </div>
          )}
        </div>
        {/* Bridge to Founders */}
        {SHOW_FOUNDERS_PROGRAM && (
          <p className="text-center mt-10 text-sm text-faded" data-testid="landing-founders-bridge">
            {t("plans_bridge_text")}{" "}
            <Link to="/founders-program" className="text-gold-light hover:text-gold underline-offset-2 hover:underline">
              {t("founders_page_label")} · {symbol}390 →
            </Link>
          </p>
        )}
      </section>

      <div className="divider-gold max-w-7xl mx-auto" />

      {/* Compare */}
      <section className="pt-12 pb-20 sm:pt-16 sm:pb-24 px-6 sm:px-8 max-w-6xl mx-auto" id="about">
        <div className="text-center mb-14">
          <h2 className="font-display text-4xl sm:text-5xl md:text-6xl mb-4">
            {t("landing_compare_title")}
          </h2>
          <p className="text-muted">{t("landing_compare_sub")}</p>
        </div>
        <div className="glass-card rounded-md overflow-hidden">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-gold-subtle">
                <th className="p-6 font-label text-faded"></th>
                <th className="p-6 font-display text-xl text-gold">KeepSoul</th>
                <th className="p-6 font-label text-faded">1Password</th>
                <th className="p-6 font-label text-faded">Google</th>
              </tr>
            </thead>
            <tbody>
              {[
                ["Cifrado AES-256", true, true, false],
                ["Mensajes del Futuro", true, false, false],
                ["Fe de Vida automática", true, false, false],
                ["Protocolo herencia", true, false, false],
                ["IA de Legado", true, false, false],
                ["Cifrado AES-256-GCM en reposo", true, true, false],
              ].map(([label, a, b, c]) => (
                <tr key={label} className="border-b border-gold-subtle/40 last:border-0">
                  <td className="p-6 text-muted">{label}</td>
                  <td className="p-6">{a ? <Check className="text-gold w-5 h-5" /> : <X className="text-faded w-5 h-5" />}</td>
                  <td className="p-6">{b ? <Check className="text-faded w-5 h-5" /> : <X className="text-faded w-5 h-5" />}</td>
                  <td className="p-6">{c ? <Check className="text-faded w-5 h-5" /> : <X className="text-faded w-5 h-5" />}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Social proof / stats */}
      <section className="py-24 sm:py-28 px-6 sm:px-8 max-w-6xl mx-auto" data-testid="landing-proof-section">
        <div className="text-center mb-14 max-w-3xl mx-auto">
          <div className="font-label text-gold mb-6">{t("landing_proof_label")}</div>
          <h2 className="font-display text-4xl sm:text-5xl md:text-6xl leading-tight">{t("landing_proof_title")}</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="glass-card rounded-md p-8 text-center" data-testid={`landing-proof-stat-${i}`}>
              <div className="font-display text-5xl md:text-6xl text-gold mb-3">
                {t(`landing_proof_stat${i}_value`)}
              </div>
              <div className="text-sm text-muted leading-relaxed">{t(`landing_proof_stat${i}_label`)}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-32 sm:py-40 px-6 sm:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-display text-5xl md:text-7xl mb-8 leading-[1.05]">
            {t("cta_final_title")}
          </h2>
          <p className="text-xl text-muted mb-12 font-body">{t("cta_final_sub")}</p>
          <Link to="/login?signup=1" data-testid="final-cta-btn">
            <button className="btn-gold">
              {t("cta_secure_legacy")} <ArrowRight className="w-4 h-4" />
            </button>
          </Link>
          <p className="mt-6 text-xs text-faded">{t("landing_final_subtle_no_founders")}</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gold-subtle py-14 px-6 sm:px-8" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 mb-10">
          <div>
            <Logo size={22} />
            <p className="text-xs text-faded mt-3 leading-relaxed">{t("landing_footer_tagline")}</p>
            <p className="text-[10px] text-faded/70 mt-2">{t("landing_footer_about")}</p>
          </div>
          <div>
            <div className="font-label text-gold mb-3 text-xs">{t("landing_footer_col_product")}</div>
            <ul className="space-y-2 text-xs text-muted">
              <li><a href="#features" className="hover:text-gold-light">{t("nav_features")}</a></li>
              <li><Link to="/plans" className="hover:text-gold-light">{t("nav_pricing")}</Link></li>
              {SHOW_FOUNDERS_PROGRAM && (
                <li><Link to="/founders-program" className="hover:text-gold-light">{t("nav_founders")}</Link></li>
              )}
              <li><a href="#security" className="hover:text-gold-light">{t("nav_security")}</a></li>
            </ul>
          </div>
          <div>
            <div className="font-label text-gold mb-3 text-xs">{t("landing_footer_col_legal")}</div>
            <ul className="space-y-2 text-xs text-muted">
              <li><Link to="/legal/privacidad" className="hover:text-gold-light">Privacidad</Link></li>
              <li><Link to="/legal/terminos" className="hover:text-gold-light">Términos</Link></li>
              <li><Link to="/legal/aviso-legal" className="hover:text-gold-light">Aviso Legal</Link></li>
              <li><Link to="/legal/cookies" className="hover:text-gold-light">Cookies</Link></li>
            </ul>
          </div>
          <div>
            <div className="font-label text-gold mb-3 text-xs">{t("landing_footer_col_support")}</div>
            <ul className="space-y-2 text-xs text-muted">
              <li><a href="mailto:support@keepsoul.app" className="hover:text-gold-light">support@keepsoul.app</a></li>
              <li><a href="mailto:dpo@keepsoul.app" className="hover:text-gold-light">dpo@keepsoul.app <span className="text-faded">(RGPD)</span></a></li>
              <li>Murcia, España</li>
              <li className="text-faded/70">ES · EN · 中 · 한</li>
            </ul>
          </div>
        </div>
        <div className="max-w-7xl mx-auto pt-6 border-t border-gold-subtle/40 text-center text-[11px] text-faded">
          © {new Date().getFullYear()} KeepSoul · AES-256-GCM · GDPR
        </div>
      </footer>
    </div>
  );
}
